import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, CreditCard, Loader2, Lock, CheckCircle2, Smartphone } from "lucide-react";
import PaymentStatusModal from "@/components/subscription/PaymentStatusModal";
import PaymentSteps from "@/components/payment/PaymentSteps";
import SecurityBadges from "@/components/payment/SecurityBadges";
import { useAuth } from '@/context/AuthContext';
import RevolutCheckout from '@revolut/checkout';
import { SubscriptionPlan, subscriptionPlans } from '@/services/subscriptions/types';

const Payment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'pending' | 'success' | 'error'>('idle');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  
  // Get plan from URL params
  const tierParam = searchParams.get('tier');
  const priceParam = searchParams.get('price');
  const nameParam = searchParams.get('name');
  const leadLimitParam = searchParams.get('leadLimit');

  // Reconstruct plan from URL params or fetch from subscriptionPlans
  const plan = React.useMemo(() => {
    if (!tierParam) return null;
    
    // Try to find the plan from the predefined plans
    const foundPlan = subscriptionPlans.find(p => p.tier === tierParam);
    if (foundPlan) return foundPlan;
    
    // Fallback: reconstruct from URL params
    if (priceParam && nameParam && leadLimitParam) {
      return {
        tier: tierParam as any,
        price: parseInt(priceParam),
        name: nameParam,
        leadLimit: leadLimitParam === 'Infinity' ? Infinity : parseInt(leadLimitParam),
        priceDisplay: `€${priceParam}`,
        features: [],
      } as SubscriptionPlan;
    }
    
    return null;
  }, [tierParam, priceParam, nameParam, leadLimitParam]);

  useEffect(() => {
    if (!plan) {
      toast({
        title: "No Plan Selected",
        description: "Please select a subscription plan first.",
        variant: "destructive",
      });
      // Don't navigate away if in new tab - user opened it intentionally
      if (window.opener) {
        window.close();
      } else {
        navigate('/subscription');
      }
    }
  }, [plan, navigate, toast]);

  const handlePayment = async () => {
    if (!plan || !user) return;
    
    // Prevent multiple attempts
    if (paymentStatus === 'processing') return;
    
    setIsProcessing(true);
    setError(null);

    try {
      console.log('Creating payment order for tier:', plan.tier);
      
      const { data, error: functionError } = await supabase.functions.invoke('create-revolut-order', {
        body: { tier: plan.tier }
      });

      if (functionError) {
        throw new Error(functionError.message || 'Failed to create payment order');
      }

      if (!data?.public_id) {
        throw new Error('No payment ID received from payment provider');
      }

      setOrderId(data.public_id);
      console.log('Initializing Revolut widget...');
      
      const mode: 'prod' | 'sandbox' = data.mode === 'production' ? 'prod' : 'sandbox';
      const RC = await RevolutCheckout(data.public_id, mode);

      if (!RC || !RC.payWithPopup) {
        throw new Error('Failed to initialize payment widget');
      }

      console.log('Opening payment popup...');

      await RC.payWithPopup({
        onSuccess: async () => {
          console.log('Payment successful, verifying...');
          
          // Show success immediately when Revolut confirms payment
          setPaymentStatus('success');
          setShowStatusModal(true);
          setIsProcessing(false);
          
          toast({
            title: "Payment Successful!",
            description: "Your subscription is now active.",
          });
          
          // Redirect to home after showing success for 2 seconds
          setTimeout(() => {
            navigate('/');
          }, 2000);
          
          // Verify in background (don't block the UI)
          try {
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-revolut-order', {
              body: { order_id: data.public_id }
            });

            if (verifyError) {
              console.error('Background verification error:', verifyError);
            } else {
              console.log('Background verification result:', verifyData);
            }
          } catch (err) {
            console.error('Background verification failed:', err);
            // No need to show error - payment was already successful in Revolut
          }
        },
        onError: (error: Error) => {
          console.error('Payment error details:', {
            message: error.message,
            name: error.name,
            stack: error.stack,
            error: error,
          });
          
          const errorMessage = error.message || 'Payment failed. Please try again.';
          setError(errorMessage);
          setPaymentStatus('error');
          setShowStatusModal(true);
          
          toast({
            title: "Payment Failed",
            description: errorMessage,
            variant: "destructive",
          });
          
          setIsProcessing(false);
        },
        onCancel: () => {
          console.log('Payment cancelled by user');
          setPaymentStatus('idle');
          setShowStatusModal(false);
          setOrderId(null);
          
          toast({
            title: "Payment Cancelled",
            description: "You can try again whenever you're ready.",
          });
          setIsProcessing(false);
        }
      });
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate payment. Please try again.';
      setError(errorMessage);
      setPaymentStatus('error');
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  if (!plan) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 md:p-6 max-w-3xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/subscription')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Subscriptions
        </Button>

        <PaymentSteps currentStep={2} />

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg flex items-start gap-3">
            <div className="mt-0.5">
              <div className="w-5 h-5 rounded-full bg-destructive/20 flex items-center justify-center">
                <span className="text-xs font-bold">!</span>
              </div>
            </div>
            <div>
              <p className="font-semibold">Payment Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold">Secure Checkout</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Complete your purchase safely with our encrypted payment system
          </p>
        </div>

        <Card className="mb-6 border-primary/20 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              Your Selected Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-xl">{plan.name} Plan</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Perfect for growing your business
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">€{plan.price}</div>
                  <div className="text-xs text-muted-foreground">per month</div>
                </div>
              </div>
              
              <div className="pt-3 border-t border-border/50">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <span className="font-medium">{plan.leadLimit} leads per month</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="h-5 w-5" />
              Available Payment Methods
            </CardTitle>
            <CardDescription>
              Choose your preferred payment option in the secure checkout window
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <CreditCard className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium text-sm">Cards</div>
                  <div className="text-xs text-muted-foreground">Credit/Debit</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Smartphone className="h-5 w-5 text-primary" />
                <div>
                  <div className="font-medium text-sm">Digital Wallets</div>
                  <div className="text-xs text-muted-foreground">Apple/Google Pay</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full h-14 text-base font-semibold mb-4"
          size="lg"
        >
          {isProcessing ? (
            <div className="flex items-center">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              <span>Opening secure checkout...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Lock className="h-5 w-5" />
              <span>Complete Secure Payment</span>
            </div>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center mb-6">
          By completing this payment, you agree to our terms of service.
          Your subscription will automatically renew each month.
        </p>

        <SecurityBadges />
      </div>

      {/* Payment Status Modal */}
      <PaymentStatusModal
        open={showStatusModal}
        status={paymentStatus}
        orderId={orderId}
        errorMessage={error || undefined}
        onClose={() => {
          setShowStatusModal(false);
          if (paymentStatus === 'success') {
            navigate('/subscription?refresh=true');
          }
        }}
        onRetry={() => {
          setShowStatusModal(false);
          setPaymentStatus('idle');
          setError(null);
          setOrderId(null);
        }}
      />
    </div>
  );
};

export default Payment;
